import React, { useContext, useEffect, useState } from "react";
import {
   Alert, Button, ButtonGroup, Col, Form, FormGroup, Input, Label,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from "reactstrap";
import { toast } from "react-toastify";
import { AiOutlineShoppingCart } from "react-icons/ai";
import NumberFormat from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { ItemDropdownMenu, SortHeader } from ".";
import { SpinnerButton } from './';
import { api, constants, helpers, reportHelpers, UserContext } from "../utils";

export default function InventoryGrid(props) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   let canEdit = userCtx && userCtx.currentUser && userCtx.currentUser.isAPUCS;
   let canSeePricing =
      userCtx &&
      userCtx.currentUser &&
      (userCtx.currentUser.isAdmin || userCtx.currentUser.isPowerUser);

   const [canOrder, setCanOrder] = useState(true);
   const [data, setData] = useState([]);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [halfYearSales, setHalfYearSales] = useState(0);
   const [monthSales, setMonthSales] = useState(0);
   const [newPrice, setNewPrice] = useState(0);
   const [priceCheckItem, setPriceCheckItem] = useState(null);
   const [priceCheckOption, setPriceCheckOption] = useState(null);
   const [quarterSales, setQuarterSales] = useState(0);
   const [updating, setUpdating] = useState(false);
   const [yearSales, setYearSales] = useState(0);
   const isCustomer = userCtx && userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole;
   let modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";

   useEffect(() => {
      if (data !== props.data) {
         setData(props.data);
         setCanOrder(props.canOrder);
         setYearSales(
            props.data.reduce(
               (total, currentItem) => (total = total + currentItem.yearSalesQty),
               0
            )
         );
         setHalfYearSales(
            props.data.reduce(
               (total, currentItem) =>
                  (total = total + currentItem.halfYearSalesQty),
               0
            )
         );
         setQuarterSales(
            props.data.reduce(
               (total, currentItem) => (total = total + currentItem.quarterSalesQty),
               0
            )
         );
         setMonthSales(
            props.data.reduce(
               (total, currentItem) => (total = total + currentItem.monthSalesQty),
               0
            )
         );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.data]);

   function allowEdit(index) {
      setEditIndex(index);
      setEditCopy(_.assign({}, data[index]));
   }

   function cancelEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }

   function currentDate() {
      let date = new Date();
      let month = date.getMonth() + 1;
      let day = date.getDate();
      let year = date.getFullYear();
      return `${month}-${day}-${year}`;
   }

   function handleChange(field, value) {
      const copyUpdate = _.assign({}, editCopy);
      copyUpdate[field] = value;
      setEditCopy(copyUpdate);
   }

   function handleInventoryPriceListPDf() {
      api
         .postDownloadBinary(
            "Report/HistoricalSales",
            { CustomerId: props.customerId, SortField: "CustomerItemNumber ASC" },
            constants.MIME_TYPES.pdf,
            `Inventory_Price_List_${props.customerId}_${currentDate()}`,
            "There was an error downloading the inventory report."
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler);
   }

   function handleLateBalance() {
      if (currentUser.isAnyCustomerTypeRole)
         toast.error("You have late unpaid invoices and must make a payment to continue or your account has been placed on hold.");
      else
         toast.error("This customer is currently on hold and no orders can be placed on this account.");
   }

   function handlePriceCheck() {
      setUpdating(true);
      const payload = {
         CustomerId: props.customerId,
         Item: priceCheckItem,
         FromPrice: priceCheckItem.sellingPrice,
         PriceLevel: props.customerId,
      };

      switch (priceCheckOption) {
         case 0:
            payload.ToPrice = newPrice;
            break;
         case 1:
            payload.ToPrice = (priceCheckItem.sellingPrice * 1.065).toFixed(2);
            break;
         case 2:
            payload.ToPrice = priceCheckItem.sellingPrice;
            break;
         default:
            break;
      }

      api
         .post("Customer/UpdateInventoryLine", payload)
         .then((response) => {
            if (response.data.success) {
               props.refreshData();
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setPriceCheckItem(null);
            setPriceCheckOption(null);
            setNewPrice(0);
         });
   }

   function updateInventoryItem() {
      setUpdating(true);
      const payload = {
         CustomerId: props.customerId,
         Item: editCopy,
         FromPrice: props.data[editIndex].sellingPrice,
         ToPrice: editCopy.sellingPrice,
         PriceLevel: props.customerId,
      };

      api
         .post("Customer/UpdateInventoryLine", payload)
         .then((response) => {
            if (response.data.success) {
               const dataCopy = _.slice(data);
               dataCopy[editIndex] = editCopy;
               setData(dataCopy);
            } else {
               if (_.isString(response?.data?.message)) {
                  Alert.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            cancelEdit();
            setUpdating(false);
         });
   }

   function validateEdit() {
      return false;
   }

   return (
      <>
         <Row className="align-items-center">
            <Col className="transparent-background">
               <div className="my-2 ms-1">
                  <Input
                     type="checkbox"
                     className="my-2"
                     checked={props.includeNoActivityItems}
                     onChange={props.setIncludeNoActivityItems}
                  />
                  <Label className="pt-1 ms-3">
                     Include Items With No Activity in Past 12 Months?
                  </Label>
               </div>
            </Col>
            <Col className="transparent-background">
               <ButtonGroup className=" float-end">
                  {data && data.length > 0 && props.exportXlsxCallback && (
                     <Button
                        title="Download as .xlsx"
                        className="shredButtonExcel float-end ms-2"
                        onClick={props.exportXlsxCallback}
                     >
                        <FontAwesomeIcon icon="file-excel" size="lg" />
                     </Button>
                  )}
                  {data && data.length > 0 && props.exportPdfCallback && (
                     <Button
                        title="Download as .pdf"
                        className="shredButton float-end"
                        onClick={props.exportPdfCallback}
                     >
                        <FontAwesomeIcon icon="file-pdf" size="lg" />
                     </Button>
                  )}
               </ButtonGroup>
            </Col>
         </Row>
         {isCustomer && props.includeNoActivityItems && (
            <Row>
               <Col>
                  <FontAwesomeIcon
                     size="2x"
                     icon="exclamation-triangle"
                     color="red"
                  />
                  <span>{` Please contact ${_.startCase(
                     props.salespersonName
                  )} for updated pricing on items with this symbol in place of the shopping cart link.`}</span>
               </Col>
            </Row>
         )}
         {data.length > 0 && (
            <Table size="sm" className="data-table">
               <thead className="table-header">
                  <tr>
                     <th style={{ width: "10%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 Customer <br /> P/N
                              </span>
                           }
                           field="customerItemNumber"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "customerItemNumber"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th>
                        <SortHeader
                           displayName={!isCustomer ? "Item" : "Item Description"}
                           field="itemNumber"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "itemNumber"}
                           callback={props.sortCallback}
                        />
                     </th>
                     {!isCustomer && (
                        <th style={{ width: "4%" }}>
                           <SortHeader
                              displayName={
                                 <span>
                                    Order <br /> Point <br /> QTY
                                 </span>
                              }
                              field="orderPointQty"
                              sortDirection={props.sortDirection}
                              sorted={props.sortField === "orderPointQty"}
                              callback={props.sortCallback}
                           />
                        </th>
                     )}
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName="Units"
                           field="unitOfMeasure"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "unitOfMeasure"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 Std <br /> QTY
                              </span>
                           }
                           field="stdCaseQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "stdCaseQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 Min <br /> Order <br /> QTY
                              </span>
                           }
                           field="realMinOrderQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "realMinOrderQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 12 Mo <br /> Usage
                              </span>
                           }
                           field="yearSalesQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "yearSalesQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 6 Mo <br /> Usage
                              </span>
                           }
                           field="halfYearSalesQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "halfYearSalesQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 3 Mo <br /> Usage
                              </span>
                           }
                           field="quarterSalesQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "quarterSalesQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName={
                              <span>
                                 1 Mo <br /> Usage
                              </span>
                           }
                           field="monthSalesQty"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "monthSalesQty"}
                           callback={props.sortCallback}
                        />
                     </th>
                     <th style={{ width: "4%" }}>
                        <SortHeader
                           displayName="Price"
                           field="sellingPrice"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "sellingPrice"}
                           callback={props.sortCallback}
                        />
                     </th>
                     {canSeePricing && (
                        <>
                           <th style={{ width: "4%" }}>
                              <SortHeader
                                 displayName="Cost"
                                 field="totalCost"
                                 sortDirection={props.sortDirection}
                                 sorted={props.sortField === "totalCost"}
                                 callback={props.sortCallback}
                              />
                           </th>
                           <th style={{ width: "4%" }}>
                              <SortHeader
                                 displayName="GPM"
                                 field="profitMargin"
                                 sortDirection={props.sortDirection}
                                 sorted={props.sortField === "profitMargin"}
                                 callback={props.sortCallback}
                              />
                           </th>
                           <th style={{ width: "4%" }}>
                              <SortHeader
                                 displayName="Percent Markup"
                                 field="markUpPercent"
                                 sortDirection={props.sortDirection}
                                 sorted={props.sortField === "markUpPercent"}
                                 callback={props.sortCallback}
                              />
                           </th>
                        </>
                     )}
                     {_.map(props.sites, (site) => {
                        return (
                           <th style={{ width: "3%" }} key={site.siteId}>
                              <SortHeader
                                 displayName={
                                    <span>
                                       {site.siteId} <br /> Avail <br /> QTY
                                    </span>
                                 }
                                 field="quantityAvailableSite1"
                                 sortDirection={props.sortDirection}
                                 sorted={props.sortField === "quantityAvailableSite1"}
                                 callback={props.sortCallback}
                              />
                           </th>
                        );
                     })}
                     <th width="4%"></th>
                     {canEdit && <th width="6%" />}
                  </tr>
               </thead>
               <tbody>
                  {_.map(data, (item, index) => {
                     return (
                        <tr
                           className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                           key={index}
                        >
                           <td
                              className="data-cell"
                              title="Print Customer Spec Report"
                              onClick={() =>
                                 reportHelpers.getCustomerSpecReport(
                                    props.customerId,
                                    item.itemNumber
                                 )
                              }
                           >
                              <span className="shredLink fw-bold cursor-pointer">
                                 {item.customerItemNumber}
                              </span>
                           </td>
                           {!isCustomer ? (
                              <ItemDropdownMenu
                                 itemNumber={item.itemNumber}
                                 itemDescription={item.itemWithDesc}
                                 attachmentCount={item.itemAttachmentCount}
                              />
                           ) : (
                              <td className="data-cell">
                                 {item.customerItemDescription}
                              </td>
                           )}
                           {!isCustomer && (
                              <td className="data-cell">
                                 {item.orderPointQty.toLocaleString()}
                              </td>
                           )}
                           <td className="data-cell">{item.unitOfMeasure}</td>
                           <td className="data-cell">{item.stdCaseQty.toLocaleString()}</td>
                           <td className="data-cell">{item.realMinOrderQty.toLocaleString()}</td>
                           <td className="data-cell">{item.yearSalesQty.toLocaleString()}</td>
                           <td className="data-cell">{item.halfYearSalesQty.toLocaleString()}</td>
                           <td className="data-cell">{item.quarterSalesQty.toLocaleString()}</td>
                           <td className="data-cell">{item.monthSalesQty.toLocaleString()}</td>
                           <td className="data-cell">
                              {editIndex === index ? (
                                 <NumberFormat
                                    className={`form-control`}
                                    style={{ width: "130px" }}
                                    prefix="$"
                                    thousandSeparator={true}
                                    decimalSeparator="."
                                    decimalScale={
                                       !item.currencyDecimals || item.currencyDecimals < 2
                                          ? 2
                                          : item.currencyDecimals
                                    }
                                    fixedDecimalScale={true}
                                    allowNegative={false}
                                    onValueChange={(vals) => {
                                       if (!vals.value) {
                                          handleChange("sellingPrice", 0);
                                       } else {
                                          handleChange(
                                             "sellingPrice",
                                             parseFloat(vals.value)
                                          );
                                       }
                                    }}
                                    value={editCopy.sellingPrice}
                                 />
                              ) : (
                                 helpers.formatCurrency(item.sellingPrice)
                              )}
                           </td>
                           {canSeePricing && (
                              <>
                                 <td className="data-cell">{`${helpers.formatCurrency(item.totalCost).toLocaleString()}`}</td>
                                 <td className="data-cell">{`${helpers.formatCurrency(item.profitMargin).toLocaleString()}`}</td>
                                 <td className="data-cell">{`${helpers.roundNum(item.markUpPercent * 100).toLocaleString()}%`}</td>
                              </>
                           )}
                           {_.map(props.sites, (site, index) => {
                              return (
                                 <td
                                    key={site.siteId}
                                    className="data-cell"
                                    onClick={
                                       !isCustomer
                                          ? () =>
                                             reportHelpers.getCaseLotInventoryReport(
                                                item.itemNumber,
                                                site.siteId
                                             )
                                          : null
                                    }
                                 >
                                    <span className={!isCustomer ? "shredLink fw-bold cursor-pointer" : ""}>
                                       {" "}
                                       {item[`quantityAvailableSite${index + 1}`].toLocaleString()}
                                    </span>
                                 </td>
                              );
                           })}
                           {item.validPrice ? (
                              <td
                                 className="data-cell"
                                 onClick={() => {
                                    canOrder ? props.addToCartCallback(item) : handleLateBalance()
                                 }}
                              >
                                 <AiOutlineShoppingCart className="cart-icon" />
                              </td>
                           ) : (
                              <td
                                 className="data-cell"
                                 onClick={ !isCustomer ? () => setPriceCheckItem(item) : null }
                              >
                                 <FontAwesomeIcon
                                    size="2x"
                                    icon="exclamation-triangle"
                                    color="red"
                                 />
                              </td>
                           )}
                           {canEdit && (
                              <td className="data-cell">
                                 <ButtonGroup>
                                    <SpinnerButton
                                       className="icon-button me-1"
                                       title={editIndex === index ? "Save changes" : "Edit line"}
                                       color={editIndex === index ? "success" : "info"}
                                       onClick={
                                          editIndex === index
                                             ? () => updateInventoryItem()
                                             : () => allowEdit(index)
                                       }
                                       disabled={
                                          (editIndex !== null && editIndex !== index) ||
                                          validateEdit() ||
                                          updating
                                       }
                                       on={
                                          //(editIndex !== null && editIndex !== index) ||
                                          validateEdit() ||
                                          updating
                                       }
                                    >
                                       <FontAwesomeIcon
                                          className="whiteIcon"
                                          icon={editIndex === index ? "check" : "edit"}
                                       />
                                    </SpinnerButton>
                                    <Button
                                       className={`icon-button ${editIndex === index ? "" : "vis-hidden"}`}
                                       color="secondary"
                                       title="Discard changes"
                                       onClick={cancelEdit}
                                       disabled={editIndex !== null && editIndex !== index}
                                    >
                                       <FontAwesomeIcon className="whiteIcon" icon="times" />
                                    </Button>
                                 </ButtonGroup>
                              </td>
                           )}
                        </tr>
                     );
                  })}
               </tbody>
               <tfoot className="table-footer">
                  <tr>
                     <td
                        className={`fw-bold`}
                        colSpan={isCustomer ? "5" : "6"}
                        children="Totals Across All Pages:"
                     />
                     <td className={`${yearSales > 0 ? "fw-bold" : ""}`}>
                        {yearSales.toLocaleString()}
                     </td>
                     <td className={`${halfYearSales > 0 ? "fw-bold" : ""}`}>
                        {halfYearSales.toLocaleString()}
                     </td>
                     <td className={`${quarterSales > 0 ? "fw-bold" : ""}`}>
                        {quarterSales.toLocaleString()}
                     </td>
                     <td className={`${monthSales > 0 ? "fw-bold" : ""}`}>
                        {monthSales.toLocaleString()}
                     </td>
                     <td />
                     <td />
                     <td />
                     {canSeePricing && <td colSpan="3" />}
                     <td colSpan={props.sites.length + 2} />
                  </tr>
               </tfoot>
            </Table>
         )}
         <Row>
            <Col>
               <div style={{ display: "flex", justifyContent: "space-between" }}>
                  {!isCustomer && (
                     <Button
                        className="shredButton"
                        children="Customer Inventory Price List"
                        onClick={handleInventoryPriceListPDf}
                     ></Button>
                  )}
                  {(currentUser?.isAdmin ||
                     currentUser?.isPowerUser ||
                     currentUser?.isCSRRole ||
                     currentUser?.isSalesRole ||
                     currentUser?.isSalesperson ||
                     currentUser?.isSewingTech ||
                     currentUser?.isMachinePartsCustomerRole) && (
                        <Button className="shredButton float-end">
                           <a
                              href={`/customer-machine-parts-pricing/${JSON.stringify({
                                 value: props.customerId,
                                 label: props.customerName,
                              })}`}
                              target="_blank"
                              rel="noreferrer"
                              className="regularAnchor text-white"
                           >
                              View/Order Machine Parts - Real Time Pricing And Availability
                           </a>
                        </Button>
                     )}
               </div>
            </Col>
         </Row>

         <Modal
            size="lg"
            isOpen={priceCheckItem ? true : false}
            contentClassName={modalClassName}
         >
            <ModalHeader
               children="Price Check"
               toggle={() => {
                  setPriceCheckItem(null);
                  setPriceCheckOption(null);
                  setNewPrice(0);
               }}
            />
            <ModalBody>
               <Row>
                  <Col xs={2}>Customer:</Col>
                  <Col xs={10}>{props.customerId}</Col>
               </Row>
               <Row>
                  <Col xs={2}>Item:</Col>
                  <Col xs={10}>{priceCheckItem && priceCheckItem.itemWithDesc}</Col>
               </Row>
               <Row>
                  <Col xs={2}>Unit Price:</Col>
                  <Col xs={10}>{priceCheckItem && helpers.formatCurrency(priceCheckItem.sellingPrice)}</Col>
               </Row>
               <hr />
               <span className="h5">Choose One of the Following Options</span>
               <Form className="mt-3">
                  <FormGroup>
                     <Row>
                        <Col xs={1}>
                           <Input
                              type="radio"
                              name="priceRadio"
                              onClick={() => setPriceCheckOption(0)}
                           />
                        </Col>
                        <Col xs={3}>Enter a New Price:</Col>
                        <Col>
                           <NumberFormat
                              className={`form-control`}
                              style={{ fontSize: ".95em" }}
                              prefix="$"
                              thousandSeparator={true}
                              decimalSeparator="."
                              decimalScale={2}
                              fixedDecimalScale={true}
                              allowNegative={false}
                              onValueChange={(vals) => {
                                 if (!vals.value) {
                                    setNewPrice(0);
                                 } else {
                                    setNewPrice(parseFloat(vals.value));
                                 }
                              }}
                              value={newPrice}
                              disabled={priceCheckOption !== 0}
                           />
                        </Col>
                     </Row>
                  </FormGroup>
                  <FormGroup>
                     <Row>
                        <Col xs={1}>
                           <Input
                              type="radio"
                              name="priceRadio"
                              onClick={() => setPriceCheckOption(1)}
                           />
                        </Col>
                        <Col>
                           Do a standard 6.5% increase to the new price of:{" "}
                           {priceCheckItem && helpers.formatCurrency(priceCheckItem.sellingPrice * 1.065)}
                        </Col>
                     </Row>
                  </FormGroup>
                  <FormGroup>
                     <Row>
                        <Col xs={1}>
                           <Input
                              type="radio"
                              name="priceRadio"
                              onClick={() => setPriceCheckOption(2)}
                           />
                        </Col>
                        <Col>
                           No price increase is desired at this time (NOTE: You must
                           still click the button to record this selection)
                        </Col>
                     </Row>
                  </FormGroup>
               </Form>
            </ModalBody>
            <ModalFooter>
               <Row>
                  <Button
                     className="shredButton float-end"
                     children="confirm"
                     onClick={() => handlePriceCheck()}
                     disabled={priceCheckOption === null}
                  />
               </Row>
            </ModalFooter>
         </Modal>
      </>
   );
}
